import React, { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Loader2, CheckCircle } from 'lucide-react'
import {
	fetchKibuCareEligibility,
	createKibuCareSession,
	KibuCareEligibilityResponse,
	KibuCareOption,
	createOrderUpgradeCheckout,
} from '@/services/orderApi'
import { currencySymbol, formatPriceWithSymbol } from '@/utils/priceFormatting'

interface KibuCareUpgradeProps {
	orderNumber: string
	token: string
	showSuccess?: boolean
	className?: string
	variant?: 'standalone' | 'modal'
	onEligibilityResolved?: (hasOptions: boolean) => void
	onChoosePlan?: (plan: KibuCareOption) => void
}

function formatPrice(amountInPence: number, currency: string): string {
	return formatPriceWithSymbol(currencySymbol(currency), amountInPence / 100)
}

function formatExpiryDate(expiryDate: string | null | undefined, planYears: number): string {
	if (!expiryDate) {
		return `${planYears} year${planYears !== 1 ? 's' : ''} from when your order ships`
	}
	const d = new Date(expiryDate)
	if (isNaN(d.getTime())) {
		return `${planYears} year${planYears !== 1 ? 's' : ''} from when your order ships`
	}
	return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

const KibuCareUpgrade: React.FC<KibuCareUpgradeProps> = ({
	orderNumber,
	token,
	showSuccess = false,
	className = '',
	variant = 'standalone',
	onEligibilityResolved,
	onChoosePlan,
}) => {
	const isModal = variant === 'modal'
	const [eligibility, setEligibility] = useState<KibuCareEligibilityResponse | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
	const [isRedirecting, setIsRedirecting] = useState(false)
	const [error, setError] = useState<string>('')
	const requestKey = useRef<string | null>(null)
	useEffect(() => { requestKey.current = null }, [orderNumber, selectedPlanId])

	useEffect(() => {
		fetchKibuCareEligibility(orderNumber, token)
			.then((data) => {
				setEligibility(data)
				onEligibilityResolved?.(data.eligible && (data.availableOptions?.length ?? 0) > 0)
			})
			.catch(() => {
				setEligibility({ eligible: false })
				onEligibilityResolved?.(false)
			})
			.finally(() => setIsLoading(false))
	}, [orderNumber, token])

	const handlePurchase = async () => {
		if (!selectedPlanId) return
		const plan = eligibility?.availableOptions?.find(option => option.planId === selectedPlanId)
		if (onChoosePlan && plan) { onChoosePlan(plan); return }
		setError('')
		setIsRedirecting(true)
		try {
			requestKey.current ||= crypto.randomUUID()
			const { sessionUrl } = eligibility?.checkoutMode === 'unified' && eligibility.careOfferId
				? await createOrderUpgradeCheckout(orderNumber, token, requestKey.current, [{ offerId: eligibility.careOfferId, lineKey: 'order', unitIndex: 0, planId: selectedPlanId }])
				: await createKibuCareSession(orderNumber, token, selectedPlanId)
			if (new URL(sessionUrl).protocol !== 'https:' || new URL(sessionUrl).hostname !== 'checkout.stripe.com') throw new Error('The secure checkout link could not be confirmed.')
			window.location.href = sessionUrl
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
			setIsRedirecting(false)
		}
	}

	if (showSuccess) {
		return (
			<div className={`rounded-lg border border-black/10 bg-kibuBlue p-4 ${className}`}>
				<div className="flex items-center gap-3">
					<CheckCircle className="size-6 text-white shrink-0" />
					<div>
						<p className="font-semibold text-white">Kibu Care activated!</p>
						<p className="text-sm text-white/80 mt-0.5">
							Your plan is now active. A confirmation email is on its way.
						</p>
					</div>
				</div>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className={`flex items-center justify-center py-6 ${className}`}>
				<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
			</div>
		)
	}

	if (!eligibility?.eligible) {
		return null
	}

	const { availableOptions = [], currentPlan, daysRemaining } = eligibility

	if (availableOptions.length === 0) return null

	const isUpgrade = availableOptions[0]?.isUpgrade

	if (isModal) {
		return (
			<div className={`rounded-2xl border border-black/10 bg-white p-5 md:p-6 ${className}`}>
				<h3 className="text-xl font-semibold text-gray-900">
					{isUpgrade ? 'Upgrade your plan' : 'Choose your plan'}
				</h3>
				{currentPlan && (
					<p className="mt-1 text-sm text-gray-500">
						You currently have a {currentPlan.planYears}-year plan. Upgrade to extend your cover.
					</p>
				)}
				{daysRemaining !== undefined && (
					<p className="mt-0.5 text-xs text-kibuBlue font-semibold">
						{daysRemaining === 1 ? '1 day' : `${daysRemaining} days`} left to {isUpgrade ? 'upgrade' : 'add cover'}
					</p>
				)}
				<div className={`mt-4 grid gap-3 ${availableOptions.length > 1 ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
					{availableOptions.map((option: KibuCareOption) => {
						const isSelected = selectedPlanId === option.planId
						return (
							<button
								type="button"
								key={option.planId}
								className={`rounded-xl border p-3 text-left transition-colors ${isSelected
									? 'border-kibuBlue bg-kibuBlue/10'
									: 'border-black/15 bg-white hover:border-black/35'
									}`}
								onClick={() => setSelectedPlanId(option.planId)}
								aria-pressed={isSelected}
							>
								<div className="flex items-center gap-3">
									<div className="relative size-16 shrink-0 overflow-hidden rounded-md bg-slate-100">
										{option.imageUrl ? (
											<img
												src={option.imageUrl}
												alt={option.optionTitle || 'Kibu Care'}
												className="h-full w-full object-cover"
											/>
										) : (
											<div className="h-full w-full bg-gradient-to-br from-slate-100 to-slate-200" />
										)}
									</div>
									<div className="min-w-0 flex-1">
										<div className="text-base font-semibold text-gray-900">{option.optionTitle}</div>
										<div className="mt-1 text-xl font-semibold text-gray-900">
											{formatPrice(option.priceAmount, option.priceCurrency)}
											{isUpgrade && <span className="text-sm font-normal ml-1 text-gray-400">to upgrade</span>}
										</div>
									</div>
								</div>
							</button>
						)
					})}
				</div>
				<p className="mt-2 text-xs text-gray-400">One plan applies to eligible products.</p>
				{error && <p className="mt-2 text-sm text-red-500">{error}</p>}
				<button
					onClick={handlePurchase}
					disabled={!selectedPlanId || isRedirecting}
					className={`mt-5 w-full rounded-xl px-4 py-3 font-semibold text-white transition-colors ${selectedPlanId && !isRedirecting
						? 'bg-kibuBlue hover:bg-kibuBlue/90'
						: 'cursor-not-allowed bg-gray-400'
						}`}
				>
					{isRedirecting ? (
						<span className="flex items-center justify-center gap-2">
							<Loader2 className="w-4 h-4 animate-spin" />
							Redirecting to checkout…
						</span>
					) : onChoosePlan ? 'Save Care choice' : isUpgrade ? (
						'Upgrade Kibu Care'
					) : (
						'Add Kibu Care to order'
					)}
				</button>
			</div>
		)
	}

	return (
		<div className={`relative rounded-lg border border-black/10 bg-kibuBlue p-3 ${className}`}>
			{/* Header */}
			<div className="flex flex-wrap items-baseline gap-x-1 gap-y-1 mb-1">
				<h3 className="text-base font-semibold leading-none text-white">Kibu Care</h3>
				{!isUpgrade && <p className="text-xs leading-none text-white">(Optional)</p>}
			</div>
			<p className="text-sm md:text-base text-white mb-1">
				{isUpgrade
					? 'Extend your accidental damage cover to 2 years.'
					: 'Protect your headphones from accidental damage.'}
			</p>
			{currentPlan && (
				<p className="text-xs text-white/70 mb-1">
					Current: {currentPlan.planYears}-year plan —{' '}
					{formatExpiryDate(currentPlan.expiryDate, currentPlan.planYears)}
				</p>
			)}
			{daysRemaining !== undefined && (
				<p className="text-[11px] text-white/60 mb-3">
					{daysRemaining === 1 ? '1 day' : `${daysRemaining} days`} left to {isUpgrade ? 'upgrade' : 'add cover'}
				</p>
			)}

			{/* Plan option cards */}
			<div className={`grid gap-3 ${availableOptions.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
				{availableOptions.map((option: KibuCareOption) => {
					const isSelected = selectedPlanId === option.planId
					return (
						<button
							type="button"
							key={option.planId}
							className={`relative w-full rounded-md border p-2 text-left transition-colors ${isSelected
								? 'border-black bg-black text-white shadow-sm'
								: 'border-black/15 bg-white hover:border-black/40'
								}`}
							onClick={() => setSelectedPlanId(option.planId)}
							aria-pressed={isSelected}
						>
							<div className="flex items-center gap-2">
								<div className="relative size-16 shrink-0 rounded-md overflow-hidden bg-slate-100">
									{option.imageUrl ? (
										<img
											src={option.imageUrl}
											alt={option.optionTitle || 'Kibu Care'}
											className="h-full w-full object-cover"
										/>
									) : (
										<div className="h-full w-full bg-gradient-to-br from-slate-100 to-slate-200" />
									)}
									{isSelected && (
										<div className="absolute inset-0 bg-kibuBlue/50 flex items-center justify-center">
											<CheckCircle2 className="size-12 text-white" />
										</div>
									)}
								</div>
								<div className="min-w-0 flex-1">
									<div className="text-sm font-semibold leading-tight">{option.optionTitle}</div>
									<div className="mt-2 text-base font-semibold leading-none">
										{formatPrice(option.priceAmount, option.priceCurrency)}
										{isUpgrade && <span className="text-xs font-normal ml-1 text-gray-400">to upgrade</span>}
									</div>
								</div>
							</div>
						</button>
					)
				})}
			</div>

			<p className="mt-1 text-[10px] text-gray-200">One plan applies to eligible products.</p>

			{error && <p className="mt-2 text-sm text-red-200">{error}</p>}

			<button
				onClick={handlePurchase}
				disabled={!selectedPlanId || isRedirecting}
				className={`mt-3 w-full rounded-xl px-4 py-3 font-semibold text-white transition-colors ${selectedPlanId && !isRedirecting
					? 'bg-black hover:bg-black/80'
					: 'cursor-not-allowed bg-black/40'
					}`}
			>
				{isRedirecting ? (
					<span className="flex items-center justify-center gap-2">
						<Loader2 className="w-4 h-4 animate-spin" />
						Redirecting to checkout…
					</span>
				) : isUpgrade ? (
					'Upgrade Kibu Care'
				) : (
					'Add Kibu Care to order'
				)}
			</button>
		</div>
	)
}

export default KibuCareUpgrade
